import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { useFilters } from '@domain-group/react-hooks'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import useAgencyProviderPackage from '@hooks/useAgencyProviderPackage'
import useAgencyProviderPackages from '@hooks/useAgencyProviderPackages'
import useSelectEntities from '@hooks/useSelectEntities'

import DashboardModuleFilters from '@components/DashboardModuleFilters'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import { confirmDelete } from '@functions/entity'

import AgencyPackageListItem from './AgencyProviderPackageListItem'

const requestOptions = {
  include_agency_provider_package_provider_package: true,
  include_provider_package_provider: true,
}

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'package_title',
      label: 'Filter by Package Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
  ]
}

const AgencyProviderPackages = (props) => {
  const { agencyId } = props

  const filterPayload = useFilters()
  const { pageFilters } = filterPayload

  const agencyPackagesPayload = useAgencyProviderPackages({
    filters: {
      agency_id: agencyId,
      ...pageFilters,
    },
    performHttpRequests: !!agencyId,
    requestOptions,
    sortKey: 'provider_package_id',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviderPackagesCount,
    filteredAgencyProviderPackages,
    hasFilteredAgencyProviderPackages,
    loading,
  } = agencyPackagesPayload

  const agencyPackagePayload = useAgencyProviderPackage({ agency_id: agencyId })
  const {
    callbacks: {
      addAgencyProviderPackages,
      bulkDeleteAgencyProviderPackages,
    },
  } = agencyPackagePayload

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      deselectAll,
      getSelectableListItemProps,
    },
    hasMinimumSelected,
    selectedEntitiesCount,
    selectedEntityIds,
  } = unselectEntitiesPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: deselectAll,
      buildActionParams: () => (
        [{ agency_provider_package_ids: selectedEntityIds, agency_id: agencyId }]
      ),
      deleteFn: bulkDeleteAgencyProviderPackages,
    },
    options: { isBulkDelete: true },
    strings: {
      deleteString: 'unassign',
      entityName: 'Provider Packages',
    },
  }

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-packages' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Packages"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredAgencyProviderPackages && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Packages present
            </DashboardModule.BlankState.Title>

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <DashboardModule.BlankState.Paragraph>
                Please assign a Package.
              </DashboardModule.BlankState.Paragraph>
            </ScopedConditionalContent>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredAgencyProviderPackages && (
          <DashboardModule.ScrollableSection maxHeight={600}>
            {filteredAgencyProviderPackages.map((agencyProviderPackage) => {
              const { id } = agencyProviderPackage
              const listItemProps = getSelectableListItemProps(id)

              return (
                <AgencyPackageListItem
                  agencyProviderPackage={agencyProviderPackage}
                  key={agencyProviderPackage.id}
                  {...listItemProps}
                />
              )
            })}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredAgencyProviderPackagesCount}
          loading={loading}
        >
          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            {hasMinimumSelected && (
              <Button
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                marginRight={[0, 'medium']}
                onClick={() => confirmDelete(confirmDeleteParams)}
                size={['medium', 'small']}
                width={['100%', 'fit-content']}
              >
                Unassign Selected Packages ({selectedEntitiesCount})
              </Button>
            )}
          </ScopedConditionalContent>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faPlus} />}
              marginTop={['medium', '0']}
              onClick={() => addAgencyProviderPackages()}
              size={['medium', 'small']}
              width={['100%', 'fit-content']}
            >
              Add Package
            </Button>
          </ScopedConditionalContent>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

AgencyProviderPackages.propTypes = {
  agencyId: PropTypes.number.isRequired,
}

export default AgencyProviderPackages
